import React, { useEffect } from 'react';
import * as S from './Styles';
import useEscapeKey from '../../../hooks/useEscapeKey';

interface ExpandedModalProps {
  modalVisible: boolean;
  toggleShowModal: () => void;
  children: React.ReactNode;
}

const ExpandedModal: React.FC<ExpandedModalProps> = ({ modalVisible, toggleShowModal, children }) => {
  const modalRef = useEscapeKey(toggleShowModal) as React.RefObject<HTMLDivElement>;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    if (modalVisible) {
      // Disable scrolling on the hidden body
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';

      document.addEventListener('keydown', handleKeyDown);
    } else {
      // Restore scrolling on the body
      document.body.style.overflow = '';
      document.body.style.position = '';

      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [modalVisible]);

  if (!modalVisible) {
    return null;
  }

  return (
    <>
      <S.ExpandedModalContainer ref={modalRef}>
        <S.ExitModalButton onClick={toggleShowModal}>
          <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.00809 22.7858C7.99235 22.4316 8.20645 22.1466 8.47408 21.879C9.78969 20.5668 11.1037 19.2521 12.4172 17.9379C12.9427 17.4122 13.4681 16.8865 13.9935 16.3611C14.3524 16.0022 14.3524 16.0006 13.9919 15.6385C13.5953 15.2416 13.199 14.8447 12.8027 14.4478C11.3506 12.9937 9.89949 11.5406 8.44259 10.0923C8.05217 9.7035 7.88844 9.26428 8.08051 8.74161C8.3686 7.95447 9.35567 7.74667 9.98381 8.34804C10.4765 8.81908 10.9565 9.30345 11.4366 9.78795C11.6562 10.0095 11.8757 10.231 12.0965 10.4513C12.3671 10.7215 12.638 10.9914 12.9089 11.2613C13.8233 12.1723 14.7379 13.0834 15.6402 14.0076C15.9094 14.2831 16.059 14.3146 16.3486 14.0202C17.4294 12.9218 18.5189 11.8327 19.6085 10.7435C20.3679 9.98443 21.1273 9.22529 21.8838 8.46297C22.2695 8.07412 22.7009 7.89307 23.2283 8.06939C24.0217 8.33702 24.2594 9.33197 23.6675 9.96168C23.2274 10.429 22.771 10.8807 22.3147 11.3323C22.1392 11.5059 21.9638 11.6795 21.7894 11.854C21.4929 12.1508 21.1967 12.4479 20.9005 12.7449C19.9137 13.7346 18.9274 14.7238 17.9308 15.7047C17.6947 15.9361 17.7041 16.0715 17.9339 16.2982C18.9208 17.2767 19.9033 18.2596 20.8857 19.2425C21.7699 20.1271 22.6541 21.0117 23.5415 21.8932C23.9304 22.2804 24.1083 22.7149 23.9241 23.2392C23.647 24.0326 22.6552 24.2577 22.0271 23.6642C21.564 23.2256 21.1155 22.7723 20.667 22.319C20.5087 22.159 20.3504 21.999 20.1915 21.8396C19.7753 21.4235 19.3597 21.0072 18.9442 20.591C18.1131 19.7585 17.2822 18.9262 16.4478 18.096C16.194 17.8438 16.0972 17.7162 15.9997 17.7156C15.9036 17.715 15.8067 17.8374 15.5583 18.085C13.7448 19.8938 11.9344 21.7074 10.1255 23.5226C9.85787 23.7902 9.57293 23.9964 9.17306 23.9964C8.51501 23.998 8.00337 23.4926 8.00651 22.7842L8.00809 22.7858Z"
              fill="#191A1F"
            />
            <path
              d="M8.00809 22.7858C7.99235 22.4316 8.20645 22.1466 8.47408 21.879C9.78969 20.5668 11.1037 19.2521 12.4172 17.9379C12.9427 17.4122 13.4681 16.8865 13.9935 16.3611C14.3524 16.0022 14.3524 16.0006 13.9919 15.6385C13.5953 15.2416 13.199 14.8447 12.8027 14.4478C11.3506 12.9937 9.89949 11.5406 8.44259 10.0923C8.05217 9.7035 7.88844 9.26428 8.08051 8.74161C8.3686 7.95447 9.35567 7.74667 9.98381 8.34804C10.4765 8.81908 10.9565 9.30345 11.4366 9.78795C11.6562 10.0095 11.8757 10.231 12.0965 10.4513C12.3671 10.7215 12.638 10.9914 12.9089 11.2613C13.8233 12.1723 14.7379 13.0834 15.6402 14.0076C15.9094 14.2831 16.059 14.3146 16.3486 14.0202C17.4294 12.9218 18.5189 11.8327 19.6085 10.7435C20.3679 9.98443 21.1273 9.22529 21.8838 8.46297C22.2695 8.07412 22.7009 7.89307 23.2283 8.06939C24.0217 8.33702 24.2594 9.33197 23.6675 9.96168C23.2274 10.429 22.771 10.8807 22.3147 11.3323C22.1392 11.5059 21.9638 11.6795 21.7894 11.854C21.4929 12.1508 21.1967 12.4479 20.9005 12.7449C19.9137 13.7346 18.9274 14.7238 17.9308 15.7047C17.6947 15.9361 17.7041 16.0715 17.9339 16.2982C18.9208 17.2767 19.9033 18.2596 20.8857 19.2425C21.7699 20.1271 22.6541 21.0117 23.5415 21.8932C23.9304 22.2804 24.1083 22.7149 23.9241 23.2392C23.647 24.0326 22.6552 24.2577 22.0271 23.6642C21.564 23.2256 21.1155 22.7723 20.667 22.319C20.5087 22.159 20.3504 21.999 20.1915 21.8396C19.7753 21.4235 19.3597 21.0072 18.9442 20.591C18.1131 19.7585 17.2822 18.9262 16.4478 18.096C16.194 17.8438 16.0972 17.7162 15.9997 17.7156C15.9036 17.715 15.8067 17.8374 15.5583 18.085C13.7448 19.8938 11.9344 21.7074 10.1255 23.5226C9.85787 23.7902 9.57293 23.9964 9.17306 23.9964C8.51501 23.998 8.00337 23.4926 8.00651 22.7842L8.00809 22.7858Z"
              stroke="#191A1F"
            />
          </svg>
        </S.ExitModalButton>
        <S.ModalBackDrop />
        <S.StyledChildrenContainer>{children}</S.StyledChildrenContainer>
      </S.ExpandedModalContainer>
    </>
  );
};

export default ExpandedModal;
